import { getLanguage } from './i18n';

// Had to update the getUtcOffset to get the timezoneOffSet
// of the date passed, not the current date
// to account for time change
export const getUtcOffset = (date: Date) => {
  return date.getTimezoneOffset() * 60 * 1000;
};

export const getDateMinusUtcOffset = (date: Date) => {
  return new Date(date.getTime() - getUtcOffset(date));
};

// YYYY-MM-DD
const yearMonthDayRegex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
export const normalizeDate = (date: Date | string) => {
  if (date instanceof Date) {
    return date;
  }
  // we have to force JS to respect the dates we pass in
  // otherwise it will convert them to the local timezone
  if (yearMonthDayRegex.test(date)) {
    date += 'T00:00:00';
  }

  return new Date(date);
};
const getMonth = (date: Date) => (date.getMonth() + 1).toString().padStart(
  2,
  '0',
);
const getDay = (date: Date) => date.getDate().toString().padStart(
  2,
  '0',
);
const getHours = (date: Date) => date.getHours().toString().padStart(
  2,
  '0',
);
const getMinutes = (date: Date) => date.getMinutes().toString().padStart(
  2,
  '0',
);
const getSeconds = (date: Date) => date.getSeconds().toString().padStart(
  2,
  '0',
);

const getStartOfDay = () => {
  const today = new Date();
  today.setHours(
    0,
    0,
    0,
    0,
  );

  return today;
};

const getEndOfDay = () => {
  const today = new Date();
  today.setHours(
    23,
    59,
    59,
    999,
  );

  return today;
};

// Exports
/**
 * @returns date string in YYYY-MM-DD format
 * @example '2018-02-07'
 */
export const formatParamDate = (date: Date | string) => {
  if (!date) {
    return '';
  }

  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }

  // TODO: we may be able to add the offset to normalizeDate but it's hard to know what
  // side effects that may have

  const normalizedDate = normalizeDate(date);

  return new Date(getDateMinusUtcOffset(normalizedDate)).toISOString().slice(0, 10);
};
/**
 * @returns date string in MM/DD/YY format based on browser language
 * @example navigator.language: 'en-US' returns '02/07/18'
 * @example navigator.language: 'fr-CA' returns '18/07/02'
 * @example navigator.language: 'fr-FR' returns '02/07/18'
 */
export const formatDate = (date: Date | null | string | undefined) => {
  if (!date) {
    return '';
  }

  return normalizeDate(date).toLocaleDateString(
    getLanguage(),
    {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    },
  );
};

/**
 * @returns date string in MM/DD/YYYY format based on browser language
 * @example navigator.language: 'en-US' returns '02/07/2018'
 * @example navigator.language: 'fr-CA' returns '2018-02-07'
 * @example navigator.language: 'fr-FR' returns '07/02/2018'
 */
export const formatDateFullYear = (date: Date | string) => {
  // When the date isn't fully typed in, return the string so that we don't get an invalid date error
  if (typeof (date) === 'string' && (date.length < 10 || !isValidDate(date))) {
    return date;
  }

  return normalizeDate(date).toLocaleDateString(
    getLanguage(),
    {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    },
  );
};

/**
 * @returns time string in hh:mm:ss a format based on browser language
 * @example navigator.language: 'en-US' returns '12:00:00 am'
 * @example navigator.language: 'fr-CA' returns '00 h 00 min 00 s'
 * @example navigator.language: 'fr-FR' returns '00:00:00'
 */
export const formatTime = (
  date: Date | string,
  hideSeconds = false,
) => {
  if (!date) {
    return '';
  }

  return normalizeDate(date).toLocaleTimeString(
    getLanguage(),
    {
      hour: '2-digit',
      minute: '2-digit',
      second: hideSeconds ? undefined : '2-digit',
    },
  ).toLowerCase();
};

/**
 * @returns time string in ha format
 * @example 1pm
 */
export const formatTimeHourPeriod = (date: Date | string) => {
  if (!date) {
    return '';
  }

  return normalizeDate(date).toLocaleTimeString(
    getLanguage(),
    {
      hour: 'numeric',
    },
  ).toLowerCase().replace(
    ' ',
    '',
  );
};

/**
 * @returns date string in MM/DD/YYYY hh:mm:ss a format based on browser language
 * @example navigator.language: 'en-US' returns '02/07/2018 12:00:00 am'
 * @example navigator.language: 'fr-CA' returns '2018-02-07 10 h 06'
 * @example navigator.language: 'fr-FR' returns '07/02/2018 10:06'
 */
export const formatDateTime = (date: Date | string) => {
  if (!date) {
    return '';
  }

  return `${formatDateFullYear(date)} ${formatTime(
    date,
    true,
  )}`;
};

/**
 * @returns date string in MM/DD h:mm a format based on browser language
 * @example navigator.language: 'en-US' returns '02/07 12:00 am'
 * @example navigator.language: 'fr-CA' returns '02-07 10 h 06'
 * @example navigator.language: 'fr-FR' returns '07/02 10:06'
 */
export const formatMonthDayTime = (date: Date | string) => {
  if (!date) {
    return '';
  }

  return normalizeDate(date).toLocaleString(
    getLanguage(),
    {
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      month: '2-digit',
    },
  ).toLowerCase().replace(
    ',',
    '',
  );
};

/**
 * @returns date string in MM/DD/YY hh:mm:ss a format based on browser language
 * @example navigator.language: 'en-US' returns '02/07/18 12:00:00 am'
 * @example navigator.language: 'fr-CA' returns '18-02-07 10 h 06 min 53 s'
 * @example navigator.language: 'fr-FR' returns '07/02/18 10:06:53'
 */
export const formatTimestamp = (date: Date | string) => {
  if (!date) {
    return '';
  }

  return normalizeDate(date).toLocaleString(
    getLanguage(),
    {
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      month: '2-digit',
      second: '2-digit',
      year: '2-digit',
    },
  ).toLowerCase().replace(
    ', ',
    ' ',
  );
};

export const minutesFromNow = (minutes: number) => new Date(new Date().getTime() + minutes * 60 * 1000);

export const isValidDate = (d: unknown) => {
  const parsedDate = Date.parse(d as string);

  return !isNaN(parsedDate);
};
export const getDateFromParamDate = (paramDate: string) => {
  let year, month, day;

  if (!isValidDate(paramDate)) {
    return null;
  }

  if (paramDate.split('-').length === 3) {
    [year, month, day] = paramDate.split('-').map(Number);
  } else {
    [month, day, year] = paramDate.split('/').map(Number);
  }

  if (year === undefined || month === undefined || day === undefined) {
    return null;
  }

  const date = new Date(
    year,
    month - 1,
    day,
  );

  return isNaN(date.getTime()) ? null : date;
};
export const getLocalServerDateTime = () => {
  return new Date().toLocaleString(
    getLanguage(),
    { timeZone: 'America/Chicago' },
  );
};

const get24HourTimeString = (date: Date) => {
  const year = date.getFullYear();
  const month = getMonth(date);
  const day = getDay(date);
  // we want 24 hour time here
  const hours = getHours(date);
  const minutes = getMinutes(date);
  const seconds = getSeconds(date);

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const calculateRangeFromDateTime = (
  amount: number,
  dateTime: string,
  formatFn: (date: Date, amount: number) => Date,
) => {
  const startDate = formatFn(
    new Date(dateTime),
    -amount,
  );
  const startDateTimeString = get24HourTimeString(startDate);
  const endDateTimeString = get24HourTimeString(new Date(dateTime));

  return { endDate: endDateTimeString, startDate: startDateTimeString };
};

export const shouldFlipDates = (startDateParam: string, endDateParam: string) => {
  // Skip flipping if partial dates are entered
  if (startDateParam.length !== 10 || endDateParam.length !== 10) {
    return false;
  }

  const startDate = getDateFromParamDate(startDateParam);
  const endDate = getDateFromParamDate(endDateParam);

  return !!startDate && !!endDate && (startDate.getTime() > endDate.getTime());
};

export const getFirstDayOfMonth = () => {
  const today = new Date();

  return new Date(
    today.getFullYear(),
    today.getMonth(),
    1,
  );
};
export const getLastDayOfMonth = () => {
  const today = new Date();

  return new Date(
    today.getFullYear(),
    today.getMonth() + 1,
    0,
  );
};
export const getThirtyDaysAgo = () => addDays(getEndOfDay(), -30);
export const getFourtyFiveDaysAgo = () => addDays(getEndOfDay(), -45);
export const getNinetyDaysAgo = () => addDays(getEndOfDay(), -90);
export const getOneYearAgo = () => addDays(getEndOfDay(), -365);

export const getSevenDaysFromNow = () => addDays(getStartOfDay(), 7);
export const getThirtyDaysFromNow = () => addDays(getStartOfDay(), 30);

export const getFirstDayOfQuarter = () => {
  const today = new Date();
  const quarter = Math.floor(today.getMonth() / 3);

  return new Date(
    today.getFullYear(),
    quarter * 3,
    1,
  );
};

export const nowInSeconds = () => Math.floor(Date.now() / 1000);

export const getDiffInDays = ({ endDate, startDate }: { endDate: Date; startDate: Date }) =>
  Math.round((normalizeDate(endDate).getTime() - normalizeDate(startDate).getTime()) / (1000 * 60 * 60 * 24));

export const getStartOfDate = (date: Date) =>
  new Date((date.toISOString().split('T')[0] ?? '') + 'T00:00:00');

export const addDays = (
  date: Date, days: number,
) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);

  return result;
};
export const addHours = (
  date: Date, hours: number,
) => {
  const result = new Date(date);
  result.setTime(result.getTime() + hours * 60 * 60 * 1000);

  return result;
};
export const addMinutes = (
  date: Date, minutes: number,
) => {
  const result = new Date(date);
  result.setTime(result.getTime() + minutes * 60 * 1000);

  return result;
};
